import React, { useEffect, useState } from 'react';

interface MarkdownViewerProps {
  markdownPath: string;
}

export default function MarkdownViewer({ markdownPath }: MarkdownViewerProps) {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    fetch(markdownPath)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load markdown: ${response.statusText}`);
        }
        return response.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [markdownPath]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading content...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-red-800">Error loading content: {error}</div>
      </div>
    );
  }

  // Simple markdown rendering - you can enhance this with react-markdown later
  // For now, we'll render as HTML with basic formatting
  const formatMarkdown = (text: string): string => {
    // Convert markdown headers to HTML
    let html = text
      .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-6 mb-3">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold mt-8 mb-4">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-8 mb-4">$1</h1>')
      // Convert bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      // Convert italic
      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
      // Convert code blocks
      .replace(/```([\s\S]*?)```/gim, '<pre class="bg-gray-100 p-4 rounded-md overflow-x-auto my-4"><code>$1</code></pre>')
      // Convert inline code
      .replace(/`([^`]+)`/gim, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm">$1</code>')
      // Convert line breaks
      .replace(/\n\n/gim, '</p><p class="mb-4">')
      .replace(/\n/gim, '<br />');

    return `<p class="mb-4">${html}</p>`;
  };

  return (
    <div 
      className="prose prose-gray max-w-none"
      dangerouslySetInnerHTML={{ __html: formatMarkdown(content) }}
    />
  );
}

